import { useCallback } from "react";
import { api } from "../api/client";
import { ALL_DOORS, useVisualizer, type DoorObject } from "../state/visualizerStore";
import { doorInputHash, effectiveSize, isBlendedFresh } from "../state/selectors";
import { sizeDims } from "../geometry/doorSize";

// Composites all configured doors by CHAINING the backend's single-door render
// (photo → door1 → door2 → … → composite). A per-door prefix cache means a change
// to door k only re-renders k and everything after it; an epoch guard discards
// results from a chain that a newer edit has superseded.
export function useRenderComposite(tenantSlug: string) {
  const renderComposite = useCallback(async () => {
    const store = useVisualizer.getState();
    const doc = store.document;
    if (!doc.photoUrl) return;

    const epoch = store.bumpEpoch();
    // A re-render replaces the composite the blends were painted on, so every blend's base is stale —
    // clear the whole map alongside integratedUrl (per-door freshness only handles input edits; base
    // changes are handled by this wholesale reset, keeping the freshness rule hash-only and simple).
    store.setRender({ status: "rendering", error: null, integratedUrl: null, integratedPreviewUrl: null, blends: {} });

    let base = doc.photoUrl;
    // Preview variant of the FINAL chain step (what the preview img shows). Only the last door's
    // preview matters — intermediate steps are inputs, never displayed.
    let preview: string | null = null;
    let rendered = false;
    try {
      for (const id of doc.doorOrder) {
        const door = doc.doors[id];
        if (!door || !door.selection.designId || !door.selection.colorId) continue;

        const dims = sizeDims(effectiveSize(door));
        const topProfile = door.topProfile ?? null;
        // See doorInputHash for what participates (and why) — the blend map keys on the same hash.
        const inputHash = doorInputHash(door, doc.collectionId);
        const cached = useVisualizer.getState().render.cache[id];
        if (cached && cached.inputHash === inputHash && cached.baseUrl === base) {
          base = cached.resultUrl;
          preview = cached.previewUrl ?? null;
          rendered = true;
          continue;
        }

        const res = await api.render(
          tenantSlug,
          {
            photoUrl: base,
            quadrilateral: door.quad,
            collectionId: doc.collectionId ?? undefined,
            designId: door.selection.designId ?? undefined,
            colorId: door.selection.colorId ?? undefined,
            windowOptionId: door.selection.windowOptionId ?? undefined,
            glassTypeId: door.selection.glassTypeId ?? undefined,
            hardwareId: door.selection.hardwareId ?? undefined,
            widthFt: dims.width,
            heightFt: dims.height,
            topProfile: topProfile ?? undefined,
            occlusionSvgPath: door.detected.occlusionSvgPath ?? undefined,
          },
          doc.sessionId,
        );
        if (useVisualizer.getState().render.epoch !== epoch) return; // superseded
        useVisualizer.getState().setCacheEntry(id, { inputHash, baseUrl: base, resultUrl: res.renderedImageUrl, previewUrl: res.previewImageUrl ?? null });
        base = res.renderedImageUrl;
        preview = res.previewImageUrl ?? null;
        rendered = true;
      }
      if (useVisualizer.getState().render.epoch !== epoch) return;
      store.setRender({ status: "idle", compositeUrl: rendered ? base : null, compositePreviewUrl: rendered ? preview : null });
    } catch {
      if (useVisualizer.getState().render.epoch !== epoch) return;
      store.setRender({ status: "error", error: "We couldn't render that. Please try again." });
    }
  }, [tenantSlug]);

  // Precise per-door AI blend on top of the current image (so prior blends/renders are preserved);
  // the backend integrates just each door's region. Individual mode blends the selected door only
  // (unchanged behavior); ALL-doors mode blends EVERY configured door by chaining integrations —
  // each door works on the previous door's result, mirroring renderComposite's chain — otherwise
  // the button would silently cover one door in the multi-door default mode.
  // Doors already blended-fresh are SKIPPED, so re-clicking after a partial result (a door the
  // server degraded to the standard render, or a door added/edited since) retries only what needs it.
  const aiBlend = useCallback(async () => {
    const store = useVisualizer.getState();
    const { document: doc, ui, render } = store;
    const ids = ui.selectedDoorId === ALL_DOORS ? doc.doorOrder : ui.selectedDoorId ? [ui.selectedDoorId] : [];
    const targets = ids
      .map((id) => doc.doors[id])
      .filter((d): d is DoorObject => !!(d && d.selection.designId && d.selection.colorId))
      .filter((d) => !isBlendedFresh(d, render.blends, doc.collectionId));
    if (targets.length === 0) return;

    let base = render.integratedUrl ?? render.compositeUrl ?? doc.photoUrl;
    if (!base) return;

    const epoch = store.bumpEpoch();
    store.setRender({ status: "blending", error: null });
    // Preview of the final blend in the chain — mirrors renderComposite's `preview`.
    let preview: string | null = null;
    // Did at least one door get a REAL AI blend this run (vs the server degrading every door to
    // the geometric composite)? Drives the reveal moment below.
    let anyRealBlend = false;
    try {
      for (const door of targets) {
        const dims = sizeDims(effectiveSize(door));
        const res = await api.integrate(
          tenantSlug,
          {
            photoUrl: base,
            quadrilateral: door.quad,
            collectionId: doc.collectionId ?? undefined,
            designId: door.selection.designId ?? undefined,
            colorId: door.selection.colorId ?? undefined,
            windowOptionId: door.selection.windowOptionId ?? undefined,
            glassTypeId: door.selection.glassTypeId ?? undefined,
            hardwareId: door.selection.hardwareId ?? undefined,
            widthFt: dims.width,
            heightFt: dims.height,
            topProfile: door.topProfile ?? undefined,
            occlusionSvgPath: door.detected.occlusionSvgPath ?? undefined,
          },
          doc.sessionId,
        );
        if (useVisualizer.getState().render.epoch !== epoch) return; // superseded
        // Record the outcome per door — usedFallback means the server returned the geometric
        // composite for this door, so it stays non-fresh and re-clicking retries exactly it.
        useVisualizer.getState().setBlendEntry(door.id, {
          inputHash: doorInputHash(door, doc.collectionId),
          baseUrl: base,
          usedFallback: res.usedFallback ?? false,
        });
        base = res.integratedImageUrl;
        preview = res.previewImageUrl ?? null;
        if (!(res.usedFallback ?? false)) anyRealBlend = true;
      }
      store.setRender({ status: "idle", integratedUrl: base, integratedPreviewUrl: preview });
      // The reveal moment: flip once into Before/After with the slider mid-frame, so what the
      // customer sees when the blend lands is the TRANSFORMATION — not a subtle in-place pixel
      // swap. Guarded so it never fights the user: only when a real blend shipped (an all-fallback
      // run shows the retry hint instead), only from edit mode (they haven't gone exploring
      // elsewhere), and never while the fullscreen editor is open under their fingers.
      const after = useVisualizer.getState();
      if (anyRealBlend && after.ui.mode === "edit" && !after.ui.fullscreen) {
        after.setComparePosition(50);
        after.setMode("compare");
      }
    } catch {
      if (useVisualizer.getState().render.epoch !== epoch) return;
      store.setRender({ status: "error", error: "AI blend failed. Please try again." });
    }
  }, [tenantSlug]);

  return { renderComposite, aiBlend };
}
